/**
 * THE CHART THEME — axes, grid, cursors, margins, motion and number formatting,
 * in one module that every panel imports.
 *
 * WHAT THIS REPLACES
 * ------------------
 * The three screens being consolidated carried the same chart furniture six
 * times over, and the copies had already drifted:
 *
 *   · `AXIS_TICK` was declared identically in five analytics panels
 *     (pulse, severity, hour-of-day, dwell, turnover) and a sixth time inline in
 *     dashboard-home-chart.tsx.
 *   · `<CartesianGrid strokeDasharray="4 4">` in the analytics panels, `"3 3"`
 *     on the reports screen — the same scaffolding drawn two ways on one product.
 *   · `<YAxis width={40}>` five times, and `width={32}` on home.
 *   · Three different tooltip cursors.
 *   · One gradient per KPI tile — six new `<defs>` on every render — plus a
 *     seventh on the home chart, all with the same two stops.
 *
 * None of that was a decision anybody made. It is what happens when six files
 * each need an axis. One module is the fix, and it is also what makes the
 * "every chart is consistent" requirement checkable rather than aspirational:
 * `chart-theme.test.ts` asserts there is exactly one of each.
 *
 * NOTHING HERE IS A LITERAL COLOUR. Everything resolves to `theme.ts`, whose
 * data-viz block is measured and guarded by `dataviz-palette.test.ts`.
 */
import { dataViz, palette, typography } from '../../../../../style-utils/theme';
import {
  clockTime,
  formatDayKey as formatKey,
  formatIn,
  zoneLabel,
} from '../../../../../function-utils/building-time';

/* ─────────────────────────────────  axes  ─────────────────────────────── */

/**
 * Scaffolding is deliberately quieter than the marks it measures.
 * `dataViz.axis` and `dataViz.grid` exist for exactly this and are already a
 * step down from `palette.slate` / `palette.line`.
 */
export const axisTick = {
  fill: dataViz.axis,
  fontSize: typography.size.xxs,
  fontFamily: typography.ui,
} as const;

/** Shared by both axes. No tick marks, no axis line — the grid does that job,
 *  and drawing it twice is ink competing with the data. */
export const axisCommon = {
  stroke: dataViz.grid,
  tick: axisTick,
  tickLine: false,
  axisLine: false,
} as const;

/**
 * `interval="preserveStartEnd"` is the default for a TIME axis: the first and
 * last buckets are the ones a reader looks for, and recharts will thin the rest
 * to fit. A CATEGORICAL axis whose every position is meaningful — the 24 hours
 * of the day, the dwell histogram's buckets — overrides it, because dropping a
 * bar there would hide the empty hour the panel exists to show.
 */
export const xAxisTime = {
  ...axisCommon,
  dy: 8,
  minTickGap: 16,
  interval: 'preserveStartEnd',
} as const;

export const xAxisCategory = {
  ...axisCommon,
  dy: 8,
} as const;

/** 40px, which fits five digits and a comma. `allowDecimals={false}` because
 *  every measure on this screen is a count of things and "2.5 entries" is not a
 *  number anybody recorded. */
export const yAxisCount = {
  ...axisCommon,
  width: 40,
  allowDecimals: false,
} as const;

/** For a second axis carrying a rate or a duration, where decimals are real. */
export const yAxisRate = {
  ...axisCommon,
  width: 48,
  allowDecimals: true,
  /* THE UNIT IS ON THE TICK, not only in the legend. An unlabelled second axis
     is what turns a dual-axis chart into a lie: the reader cannot tell which
     series it belongs to, so they assume it belongs to the one they were
     looking at. */
  tickFormatter: (value: number) => `${value}%`,
} as const;

export const grid = {
  stroke: dataViz.grid,
  strokeDasharray: '4 4',
  /** Horizontal only. Vertical gridlines on a categorical axis fence every
   *  category into a box and double the scaffolding for nothing. */
  vertical: false,
} as const;

/* ────────────────────────────────  cursor  ───────────────────────────── */

/**
 * The hover cursor, per chart family. Recharts' default is a grey box that
 * belongs to no design system; naming both variants here is what stops a third
 * appearing.
 */
export const cursor = {
  /** A tinted column behind the hovered bar. */
  bar: { fill: palette.irisTintSoft },
  /** A hairline down the hovered point. */
  line: { stroke: palette.iris, strokeOpacity: 0.25, strokeWidth: 1 },
  /** Scatter and pie read their own hover emphasis from activeShape. */
  none: false,
} as const;

/* ────────────────────────────────  margins  ──────────────────────────── */

/**
 * Chart margins. The right margin exists so the last x tick is not clipped; the
 * left is 0 because the y axis reserves its own width.
 */
export const margins = {
  default: { top: 12, right: 12, left: 0, bottom: 4 },
  /** Room above the plot for a ReferenceLine label (p50/p90, a target). */
  withTopLabels: { top: 20, right: 12, left: 0, bottom: 4 },
  /** A phone has no spare 8px on the right. */
  mobile: { top: 12, right: 8, left: 0, bottom: 4 },
  /** A brush occupies the bottom of the plot area. */
  withBrush: { top: 12, right: 12, left: 0, bottom: 24 },
  /** A second y-axis needs its own width on the right, or its labels are cut in
   *  half — which on a percentage axis means "1" where the value is "12". */
  dualAxis: { top: 12, right: 4, left: 0, bottom: 4 },
} as const;

/* ─────────────────────────────────  marks  ───────────────────────────── */

/** 4px rounded data-end, anchored to the baseline: the top two corners round,
 *  the two at the axis stay square. */
export const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

/** A 2px surface gap between adjacent marks, so 24 bars read as 24 marks and
 *  not as one shape with notches. */
export const markSeparator = {
  stroke: palette.surface,
  strokeWidth: 2,
} as const;

export const lineMark = {
  type: 'monotone',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  dot: false,
} as const;

export const activeDot = (tone: string) => ({
  r: 4,
  fill: tone,
  stroke: palette.surface,
  strokeWidth: 2,
});

/* ────────────────────────  the one-bucket period  ─────────────────────── */

/**
 * A SERIES OF ONE POINT HAS NO LINE TO DRAW, SO IT IS DRAWN AS A BAR.
 *
 * ── THE BUG, AND THE ROUND THAT DID NOT FIX IT ──
 * Reported as "dashboard nothing works, I see no graphics here", on a period of
 * Sep 4 to Sep 4 — a single day, a single bucket, one data point. The axes, the
 * ticks, the mean reference line and the whole caption rendered; the reading did
 * not. Reported a SECOND time, unchanged, as "this is still empty and displays
 * nothing".
 *
 * ── WHAT IS ACTUALLY TRUE OF recharts 2.15.4, MEASURED ──
 * With one datum it emits NO series path: no `.recharts-line-curve`, no
 * `.recharts-area-area`, no `.recharts-area-curve`. That much the first round
 * got right, and it is geometry rather than a defect — a path through one point
 * has zero length and the area under it has zero area.
 *
 * What the first round got WRONG is the conclusion it drew from that. Both
 * `Line` and `Area` render `(hasSinglePoint || dot) && this.renderDots(...)`
 * (Line.js:396, Area.js:376), so a one-point series has ALWAYS carried a dot —
 * a default `r=3`, white-filled, series-stroked speck — even under the
 * `dot={false}` every panel here sets. The panel was never emitting nothing. It
 * was emitting a six-pixel speck in a nine-hundred-pixel-wide chart, which a
 * reader calls "nothing" and is right to.
 *
 * So enlarging that speck to `r=6` — what the first round did — could not have
 * worked, and did not: 12px of ink in a 900x340 frame still reads as an empty
 * chart with a stray dot in it.
 *
 * ── THE FIX: CHANGE THE MARK, NOT ITS SIZE ──
 * One bucket is not a shape over time, and no amount of dot radius makes it one.
 * It IS a quantity, and the mark for a quantity in a category is a bar: it sits
 * on the baseline, it has width, and it is read the same way by everybody. So a
 * one-bucket period switches the series from a line/area to a `<Bar>`:
 *
 *     {oneBucket(points.length) ? (
 *       <Bar dataKey="value" fill={tone} {...oneBucketBar} />
 *     ) : (
 *       <Line dataKey="value" {...lineMark} />
 *     )}
 *
 * `maxBarSize` is what keeps this a BAR rather than a wall: with a single
 * category recharts gives the band the entire plot width, and a 900px-wide block
 * of colour is its own kind of unreadable.
 *
 * ── soloPoint SURVIVES, FOR THE CHARTS THAT CANNOT TAKE A BAR ──
 * `<AreaChart>` and `<LineChart>` reject a `<Bar>` child — only `<ComposedChart>`
 * accepts one — so the sparkline tiles keep the enlarged dot. A dot IS legible
 * in a 60px sparkline; that was never the case being reported.
 */
export const oneBucket = (pointCount: number): boolean => pointCount === 1;

/**
 * The shared shape of the one-bucket bar. `isAnimationActive: false` because a
 * bar that grows out of the axis is a flourish on a chart whose entire content
 * is that one bar, and `firstPaintAnimation` is keyed per panel rather than per
 * mark — a mark that appears only in this one case must not consume the panel's
 * single animated paint.
 */
export const oneBucketBar = {
  radius: BAR_RADIUS,
  maxBarSize: 72,
  isAnimationActive: false,
} as const;

/**
 * The comparison bar that stands beside it — the same muted grey the comparison
 * LINE uses everywhere else, so the two readings keep their identities when the
 * mark changes underneath them.
 */
export const oneBucketCompareBar = {
  ...oneBucketBar,
  fill: dataViz.axis,
  fillOpacity: 0.45,
} as const;

/**
 * THE FALLBACK MARK, for a one-point series in a chart that cannot hold a bar.
 *
 * Dots on a thirty-bucket line are noise, and the panels turned them off for
 * that reason. So the dot comes back only when it is the ENTIRE chart, which is
 * exactly when it stops being decoration. Spread this over the series instead of
 * writing `dot={false}`:
 *
 *     <Area dataKey="value" {...soloPoint(points.length, tone)} />
 *
 * It is bigger than `activeDot` on purpose — this mark IS the reading, not a
 * hover state — and it takes the surface colour as a halo so it stays legible
 * against the grid in both themes.
 *
 * A ZERO-LENGTH SERIES gets `dot: false` like any other: there is no point to
 * draw, and the panel's own empty state is what speaks.
 */
export const soloPoint = (
  pointCount: number,
  tone: string
): { dot: false | { r: number; fill: string; stroke: string; strokeWidth: number } } =>
  oneBucket(pointCount)
    ? { dot: { r: 6, fill: tone, stroke: palette.surface, strokeWidth: 2 } }
    : { dot: false };

/**
 * THE CAVEAT THAT GOES WITH THE SWITCHED MARK.
 *
 * A reader who scrubbed a range down to one day and got a bar back is owed the
 * reason the chart stopped being a line, or the next thing they will report is
 * that the chart type changes at random.
 */
export const ONE_BUCKET_NOTE =
  'This period covers a single bucket, so it is drawn as a bar rather than a ' +
  'line: a line needs two readings before it has a shape, and one reading has ' +
  'no trend to show.';

/**
 * THE COMPARISON SERIES, EVERYWHERE.
 *
 * Muted, dashed, on the SAME axes as the series it compares against — never a
 * second chart. `connectNulls` because the earlier period is a reference line
 * rather than a reading: a gap in it must not break the shape a reader is
 * comparing to. `activeDot: false` because the tooltip already carries its
 * value and a second highlighted dot competes with the real one.
 */
export const compareSeries = {
  ...lineMark,
  stroke: dataViz.axis,
  strokeDasharray: '5 4',
  activeDot: false,
  connectNulls: true,
} as const;

/**
 * A REFERENCE LINE — the average, a threshold, a target.
 *
 * ONE dash pattern for the whole screen. The analytics panels drew "4 4" for
 * this and the reports screen drew "3 3", which made two lines that mean the
 * same thing look like two different kinds of line. It is deliberately NOT the
 * grid's pattern and NOT the comparison series' pattern: three dashed things on
 * one chart have to be told apart.
 */
export const referenceLine = {
  stroke: palette.lineStrong,
  strokeDasharray: '6 3',
} as const;

/** The label that rides on a reference line. */
export const referenceLabel = {
  fill: palette.slateLight,
  fontSize: 10,
} as const;

/* ────────────────────────────────  motion  ───────────────────────────── */

/**
 * ANIMATE ON FIRST PAINT ONLY.
 *
 * A chart that re-animates on every filter change flickers while somebody
 * scrubs a control, which makes a fast filter feel slow and an incremental
 * narrowing impossible to follow. The three screens disagreed about this: the
 * home chart animated on every render (900ms), every analytics panel set
 * `isAnimationActive={false}` outright, and the reports charts left the recharts
 * default on.
 *
 * So it is controlled centrally: a panel animates the first time it renders and
 * never again, and `prefers-reduced-motion` turns it off entirely.
 */
const painted = new Set<string>();

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const firstPaintAnimation = (
  panelId: string
): { isAnimationActive: boolean; animationDuration: number; animationEasing: 'ease-out' } => {
  const first = !painted.has(panelId) && !prefersReducedMotion();
  painted.add(panelId);

  return {
    isAnimationActive: first,
    animationDuration: 640,
    animationEasing: 'ease-out',
  };
};

/** Test seam. Nothing in the application calls it. */
export const resetFirstPaint = (): void => painted.clear();

/* ───────────────────────────────  sync id  ───────────────────────────── */

/**
 * Every time-series panel shares this, so hovering one moves the crosshair on
 * all of them.
 *
 * NOTE, because it is the thing that surprises people: in recharts 2.x `syncId`
 * synchronises the TOOLTIP AND THE CURSOR and nothing else. It does not
 * synchronise a `<Brush>`. That is why the brush is lifted into the filter
 * object rather than left to recharts — see charts/use-chart-brush.ts.
 */
export const SYNC_ID = 'dashboard-time';

/* ────────────────────────────  gradient ids  ─────────────────────────── */

/**
 * Defined ONCE, in <ChartDefs/>, and referenced by id. The KPI strip used to
 * mint one `<linearGradient>` per tile — six new defs on every render, all
 * identical — plus a seventh on the home chart.
 */
export const GRADIENT = {
  area: 'dash-area',
  areaCompare: 'dash-area-compare',
  hatchUnrecorded: 'dash-hatch-unrecorded',
  anomalyGlow: 'dash-anomaly-glow',
} as const;

export const fillUrl = (id: string): string => `url(#${id})`;

/* ──────────────────────────────  formatting  ─────────────────────────── */

/**
 * THE FORMATTERS, PORTED UNCHANGED FROM analytics-shared.tsx.
 *
 * Each one carries a rule that has already been got wrong once, and the rules
 * are the reason they are shared rather than re-derived per panel.
 */

/** `hour_of_day` -> `Hour of day`. Derived rather than mapped, because a map is
 *  a second copy of the server's dimension list and would go stale the first
 *  time a dimension is added. */
export const humanise = (key: string): string => {
  const words = String(key || '').replace(/_/g, ' ').trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/**
 * An item's value as a number, or null.
 *
 * `last_activity` (agg `max_at`) puts an ISO instant in `value`, and
 * `Number('2026-08-01T…')` is NaN — which is why this refuses a non-finite
 * result rather than letting NaN reach a bar width.
 */
export const numberOf = (value: number | string | null): number | null => {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(n) ? n : null;
};

export const formatCount = (value: number | null): string =>
  value == null ? '—' : value.toLocaleString();

/** Plural agreement from the measure's own noun pair, so "1 entry" is never
 *  "1 entries". The server ships the pair for exactly this. */
export const withNoun = (value: number, noun: [string, string]): string =>
  `${value.toLocaleString()} ${value === 1 ? noun[0] : noun[1]}`;

const HOUR_MS = 3600000;
const DAY_MS = 86400000;

/** A span in the largest unit that stays honest. Used where two windows are
 *  DIFFERENT LENGTHS and the reader is shown both instead of a percentage. */
export const formatSpan = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return '0';
  if (ms % DAY_MS === 0) {
    const days = ms / DAY_MS;

    return `${days} day${days === 1 ? '' : 's'}`;
  }
  const hours = Math.round(ms / HOUR_MS);
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`;

  return `${Math.max(1, Math.round(ms / 60000))} min`;
};

/**
 * An instant, in a NAMED zone — the zone the RESPONSE carries (`tz`), which is
 * the clock the server cut the window and every bucket on (R-T12: the reader's
 * for an owner or a manager). The zone is passed explicitly: a caption rendered
 * in any other zone would disagree with the numbers underneath it. A missing or
 * unusable zone falls back in the one time module, never throws.
 */
export const formatInstant = (iso: string | null, tz: string): string =>
  formatIn(iso, tz, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }) || '—';

export const formatDay = (iso: string | null, tz: string): string =>
  formatIn(iso, tz, { month: 'short', day: 'numeric' }) || '—';

/**
 * A `dimension=day` group key is ALREADY a local YYYY-MM-DD on the response's
 * clock, so it is formatted as a plain date and NOT re-zoned — passing it
 * through a timezone conversion would shift the label off its bucket.
 */
export const formatDayKey = (key: string): string => formatKey(key);

/**
 * The local clock time a window ENDS at, but only when that is not midnight.
 *
 * Returns null on a day boundary, which is the signal a caller wants: a rolling
 * range ends at the instant of the request, so its last calendar bucket is a
 * fraction of a day and a chart of whole days draws that fraction as a cliff. A
 * calendar range (`yesterday`, `last_month`) ends at midnight and has no such
 * bucket, so it must NOT carry the caveat — a warning that is always on is a
 * warning nobody reads.
 */
export const partialDayEndsAt = (
  to: string | null | undefined,
  tz: string
): string | null => {
  if (!to) return null;
  const clock = clockTime(to, tz);
  // '00:00' and '24:00' are both how a midnight boundary can come back,
  // depending on the runtime's hourCycle.
  if (!clock || clock === '00:00' || clock === '24:00') return null;
  const where = zoneLabel(tz);

  return where ? `${clock} ${where === 'UTC' ? 'UTC' : `${where} time`}` : clock;
};

/**
 * A COUNT OF SECONDS AS SOMETHING A DESK MANAGER READS.
 *
 * The honest unit changes with the magnitude: a visit is minutes, a parcel is
 * days, and printing "0.3 h" for a twenty-minute visit is a number somebody has
 * to convert in their head before they can act on it.
 *
 * The headline percentiles do NOT go through here — they render from the
 * server's own `hoursView`, so the figure on the screen and the figure in the
 * response cannot drift apart by a rounding rule.
 */
export const formatSeconds = (secs: number | null): string => {
  if (secs == null || !Number.isFinite(secs)) return '—';
  if (secs < 60) return `${Math.round(secs)} sec`;
  if (secs < 3600) return `${Math.round(secs / 60)} min`;
  if (secs < 86400) return `${Math.round((secs / 3600) * 10) / 10} h`;
  const days = Math.round((secs / 86400) * 10) / 10;

  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

/** Hours, from the server's own `hoursView`. Kept separate from formatSeconds
 *  so the unit shown is the unit the server converted to. */
export const formatHoursValue = (hours: number | null): string =>
  hours == null ? '—' : `${hours.toLocaleString()}`;

/**
 * A RATIO THE SERVER ALREADY EXPRESSED AS A PERCENTAGE.
 *
 * `ratioOf` in metrics.js is `Math.round((n / d) * 1000) / 10` — so 67.3 means
 * 67.3%, already multiplied. Multiplying again here is the single easiest way to
 * put "6730%" on a dashboard, so the conversion lives in one place and is
 * documented at it.
 *
 * NULL RENDERS AS AN EM DASH, NEVER AS ZERO. The server sends null for a zero
 * denominator on purpose: 0/0 drawn as 0% reads as "nothing was scanned" when
 * the truth is "nothing was recorded at all", and only one of those is a fact.
 */
export const formatRatio = (ratio: number | null): string =>
  ratio == null ? '—' : `${ratio.toLocaleString()}%`;

/** `n / d`, printed. The raw pair travels beside every ratio on this screen
 *  because a percentage without its denominator is a percentage nobody can
 *  size: 67% of three entries and 67% of three thousand are the same number and
 *  different evidence. */
export const formatFraction = (numerator: number, denominator: number): string =>
  `${numerator.toLocaleString()} / ${denominator.toLocaleString()}`;

/** A signed absolute change, for a delta chip. */
export const formatDelta = (delta: number | null): string =>
  delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`;

/** A share of a total, as a percentage. Null total is an em dash, never 0%. */
export const formatShare = (value: number, total: number): string =>
  total > 0 ? `${Math.round((value / total) * 100)}%` : '—';

export const format = {
  humanise,
  numberOf,
  count: formatCount,
  withNoun,
  span: formatSpan,
  instant: formatInstant,
  day: formatDay,
  dayKey: formatDayKey,
  partialDayEndsAt,
  seconds: formatSeconds,
  hours: formatHoursValue,
  ratio: formatRatio,
  fraction: formatFraction,
  delta: formatDelta,
  share: formatShare,
} as const;

/* ────────────────────────────  responsive shape  ─────────────────────── */

/**
 * A SIMPLIFIED CHART UNDER ~480px.
 *
 * Recharts draws in SVG, so no media query can reach inside a chart — the
 * decision has to arrive as a prop. This is the same reason
 * dashboard-home-chart.tsx and analytics-composition.tsx already take
 * `isMobile`; here it is one object rather than a scatter of ternaries.
 */
export interface ChartMargin {
  top: number;
  right: number;
  left: number;
  bottom: number;
}

export interface ChartShape {
  margin: ChartMargin;
  /** Fewer ticks, so labels do not collide into a smear. */
  tickInterval: number | 'preserveStartEnd';
  /** The brush is a precision control and there is no room for one. */
  showBrush: boolean;
  /** Direct labels on a pie become a smear of leader lines at this width. */
  directLabels: boolean;
  /** The legend becomes a tappable list under the chart. */
  legendBelow: boolean;
}

export const shapeFor = (isMobile: boolean): ChartShape =>
  isMobile
    ? {
        margin: margins.mobile,
        tickInterval: 'preserveStartEnd',
        showBrush: false,
        directLabels: false,
        legendBelow: true,
      }
    : {
        margin: margins.default,
        tickInterval: 'preserveStartEnd',
        showBrush: true,
        directLabels: true,
        legendBelow: false,
      };
